import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import LayoutBase from '../../shared/layouts';
import { api } from '../../shared/services/api';
import { useAuthContext } from '../../shared/contexts/authContext';
import Loading from '../../components/loading';

interface StaCredential {
  id: string;
  user_id: string;
  sta_username: string;
  sta_password: string;
  date_expiration: string;
  sta_status: string;
}

// --- Seção de Alterar Senha ---

const ChangePasswordSection = () => {
  const token = Cookies.get('@token');

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required('Senha atual é obrigatória'),
      newPassword: Yup.string()
        .min(6, 'Mínimo 6 caracteres')
        .required('Nova senha é obrigatória'),
      confirmNewPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'As senhas não conferem')
        .required('Confirmação é obrigatória'),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        await api.put(
          '/users/changePassword',
          {
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            confirmNewPassword: values.confirmNewPassword,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          },
        );
        toast.success('Senha alterada com sucesso!');
        resetForm();
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string } } };
        toast.error(error.response?.data?.message || 'Erro ao alterar senha');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LockResetIcon color="primary" />
        <Typography variant="h6">Alterar Senha</Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
          <TextField
            label="Senha Atual"
            type="password"
            size="small"
            name="currentPassword"
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
            helperText={formik.touched.currentPassword && formik.errors.currentPassword}
          />
          <TextField
            label="Nova Senha"
            type="password"
            size="small"
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
          <TextField
            label="Confirmar Nova Senha"
            type="password"
            size="small"
            name="confirmNewPassword"
            value={formik.values.confirmNewPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmNewPassword && Boolean(formik.errors.confirmNewPassword)}
            helperText={formik.touched.confirmNewPassword && formik.errors.confirmNewPassword}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            sx={{ alignSelf: 'flex-start' }}
          >
            Alterar Senha
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

// --- Seção de Credenciais STA ---

const StaCredentialsSection = () => {
  const token = Cookies.get('@token');
  const { userId } = useAuthContext();
  const [credentials, setCredentials] = useState<StaCredential[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState<StaCredential | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const headers = {
    Authorization: 'Bearer ' + token,
    'Content-Type': 'application/json',
  };

  const fetchCredentials = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/sta/credentials', { headers });
      setCredentials(response.data?.data || response.data || []);
    } catch {
      toast.error('Erro ao buscar credenciais');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formik = useFormik({
    initialValues: {
      sta_username: '',
      sta_password: '',
      date_expiration: '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      sta_username: Yup.string().required('Usuário STA é obrigatório'),
      sta_password: Yup.string().required('Senha STA é obrigatória'),
      date_expiration: Yup.string().required('Data de expiração é obrigatória'),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if (editingCredential) {
          await api.put(
            `/sta/credentials/${editingCredential.id}`,
            {
              sta_username: values.sta_username,
              sta_password: values.sta_password,
              date_expiration: values.date_expiration,
            },
            { headers },
          );
          toast.success('Credencial atualizada!');
        } else {
          await api.post(
            '/sta/credentials',
            {
              user_id: userId,
              sta_username: values.sta_username,
              sta_password: values.sta_password,
              date_expiration: values.date_expiration,
            },
            { headers },
          );
          toast.success('Credencial criada!');
        }
        resetForm();
        setDialogOpen(false);
        setEditingCredential(null);
        fetchCredentials();
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string } } };
        toast.error(error.response?.data?.message || 'Erro ao salvar credencial');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleOpenCreate = () => {
    setEditingCredential(null);
    formik.resetForm({
      values: { sta_username: '', sta_password: '', date_expiration: '' },
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (cred: StaCredential) => {
    setEditingCredential(cred);
    formik.resetForm({
      values: {
        sta_username: cred.sta_username,
        sta_password: '',
        date_expiration: cred.date_expiration?.split('T')[0] || '',
      },
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/sta/credentials/${deletingId}`, { headers });
      toast.success('Credencial removida!');
      fetchCredentials();
    } catch {
      toast.error('Erro ao remover credencial');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('pt-BR');
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon color="primary" />
          <Typography variant="h6">Credenciais STA</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={handleOpenCreate}
        >
          Nova Credencial
        </Button>
      </Box>

      <Loading isLoading={isLoading} />

      {credentials.length === 0 && !isLoading ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
          Nenhuma credencial cadastrada
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuário STA</TableCell>
                <TableCell>Senha</TableCell>
                <TableCell>Validade</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentials.map((cred) => (
                <TableRow key={cred.id} hover>
                  <TableCell>{cred.sta_username}</TableCell>
                  <TableCell>{cred.sta_password}</TableCell>
                  <TableCell>{formatDate(cred.date_expiration)}</TableCell>
                  <TableCell>{cred.sta_status || '-'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(cred)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeletingId(cred.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingCredential ? 'Editar Credencial' : 'Nova Credencial'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Usuário STA"
              size="small"
              name="sta_username"
              value={formik.values.sta_username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.sta_username && Boolean(formik.errors.sta_username)}
              helperText={formik.touched.sta_username && formik.errors.sta_username}
            />
            <TextField
              label="Senha STA"
              type="password"
              size="small"
              name="sta_password"
              value={formik.values.sta_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.sta_password && Boolean(formik.errors.sta_password)}
              helperText={formik.touched.sta_password && formik.errors.sta_password}
            />
            <TextField
              label="Data de Expiração"
              type="date"
              size="small"
              name="date_expiration"
              value={formik.values.date_expiration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.date_expiration && Boolean(formik.errors.date_expiration)}
              helperText={formik.touched.date_expiration && formik.errors.date_expiration}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
              {editingCredential ? 'Salvar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog confirmar exclusão */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Deseja realmente excluir esta credencial?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// --- Componente Principal ---

const Profile = () => {
  const { userEmail } = useAuthContext();

  return (
    <LayoutBase>
      <Typography color="textPrimary" variant="h5">
        Meu Perfil
      </Typography>
      <Typography sx={{ mb: 2, color: 'text.secondary' }}>{userEmail}</Typography>

      <ChangePasswordSection />
      <StaCredentialsSection />
    </LayoutBase>
  );
};

export default Profile;
